import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { DEF_PG_SIZE_WIZARD } from "../../../../constants/maximum-page-size";
import { MAX_RECORDS } from "../../../../constants/notification-key";
import { useLocale } from "../../../../contexts/localeContext";
import { TableContextProvider } from "../../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import DoorColumns from "../../../../models/ColumnDefinitions/DoorColumns";
import { ListData } from "../../../../models/ListData";
import { ListQuery } from "../../../../models/ListQuery";
import { Lookup } from "../../../../models/Lookup";
import LookupTypes from "../../../../models/LookupTypes";
import ControllerSearch from "../../../../models/search/ControllerSearch";
import DoorSearch from "../../../../models/search/DoorSearch";
import SiteSearch from "../../../../models/search/SiteSearch";
import ZoneSearch from "../../../../models/search/ZoneSearch";
import { getControllerNames } from "../../../../services/controllerService";
import { getDoors } from "../../../../services/doorService";
import { getSiteNamesWithPagination } from "../../../../services/siteService";
import { getZoneNamesWithPagination } from "../../../../services/zoneService";
import { appendFilter } from "../../../../utilities/filters";
import AssignWizard from "../../../assignWizard/AssignWizard";
import TableTab from "../../../tabs/table-tab";
import { toast } from "react-toastify";

interface FilterByDoorProps {
    manageItems: (selectedIds: string[], oldSelectedIds: string[]) => void;
}

const FilterByDoor: FC<FilterByDoorProps> = ({ manageItems }) => {
    const { translate } = useLocale();
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const { values } = useFormikContext<any>();
    
    useEffect(() => {
        setSelectedIds(values.filterIds);
        setOldSelectedIds(values.filterIds);
        dataRefresh(getDataMethod())
    }, [values.filterIds])

    useEffect(() => {
        setSelectedIds(values.filterIds);
        setOldSelectedIds(values.filterIds);
    }, [showManagedModal])

    const lookups: Lookup[] = [
        {
            name: LookupTypes.zoneName,
            values: [],
            dataMethod: getZoneNamesWithPagination,
            searchAccessor: ZoneSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.zoneController,
            values: [],
            dataMethod: getControllerNames,
            searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.zoneSite,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    const getDataMethod = (): (listParameter?: ListQuery) => Promise<ListData> => {
        if (values.filterIds && values.filterIds.length) {
            return (listParameters?: ListQuery) => getDoors({
                ...listParameters, filters: appendFilter(
                    values.filterIds?.toString(),
                    "Items",
                    "id",
                    listParameters?.filters ?? [],
                    "=in="
                )
            })
        }

        return () => {
            return new Promise((resolve) => {
                resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 1, totalCount: 0, totalPages: 0 } })
            })
        }
    }

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const onSelectedIds = (ids: string[]) => {
        if (ids.length >= MAX_RECORDS) {
            toast.error(translate.byKey(TranslationKeys.aMaximimumOf20RecordsCanBeSelected));
            setSelectedIds([...selectedIds])
        } else {
            setSelectedIds([...ids])
        }
    }

    return <>
        <TableContextProvider
            dataMethod={getDataMethod()}
            useTable
            usePagination
            useFilter
            useSearch
            useSort
            tableDescriptor={DoorColumns}
            searchDescriptor={DoorSearch}
            maxPageSize={DEF_PG_SIZE_WIZARD}
            manualDataRefresh={manualDataRefresh}
            lookup={lookups}
            canExportReport={false}>
            <TableTab
                title={translate.byKey(TranslationKeys.door)}
                selectRows={false}
                tableId={'filterByDoorTable'}
                cyName="filterByDoor">
                <button
                    type="button"
                    className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                    onClick={() => setShowManagedModal(true)}>
                    {translate.byKey(TranslationKeys.manage)}
                </button>
            </TableTab>
        </TableContextProvider>
        <AssignWizard
            isOpen={showManagedModal}
            onClose={() => setShowManagedModal(false)}
            title={translate.byKey(TranslationKeys.door)}
            lookup={lookups}
            dataMethod={getDoors}
            columnDescriptors={{
                columns: DoorColumns,
                searchColumns: DoorSearch
            }}
            handleOnSubmit={() => manageItems(selectedIds, oldSelectedIds)}
            selectedIds={selectedIds}
            setSelectedIds={onSelectedIds} />
    </>
}

export default FilterByDoor;
